import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Read-only evidence from the healing service: the relevance gate result and the
// hold-out verdict. Both are context for the reviewer, not a decision, so they use
// the same neutral grey as the NOT_APPLICABLE validation card and never red or
// green (a hold-out PASS is not an approval signal).

const GREY = '#94a3b8';
const BOX_SX = {
  mx: 3, mt: 2, px: 2, py: 1.5, borderRadius: 1.5,
  background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.25)',
};

const MAX_TEXT = 300;
const clip = (v) => {
  const t = String(v);
  return t.length > MAX_TEXT ? `${t.slice(0, MAX_TEXT)}…` : t;
};

function CardShell({ title, children }) {
  return (
    <Box sx={BOX_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <InfoOutlinedIcon sx={{ color: GREY, fontSize: 20 }} />
        <Typography sx={{ color: '#e2e8f0', fontSize: '0.85rem', fontWeight: 600 }}>{title}</Typography>
      </Box>
      {children}
    </Box>
  );
}

export function RelevanceGateCard({ gate }) {
  if (!gate || typeof gate !== 'object') return null;
  const checks = Array.isArray(gate.checks) ? gate.checks : [];
  const codes = Array.isArray(gate.reasonCodes) ? gate.reasonCodes : [];
  return (
    <CardShell title="Relevance gate">
      <Typography sx={{ color: GREY, fontSize: '0.78rem' }}>
        Mode: {gate.mode || 'unknown'}
        {gate.evaluatedAt ? ` · evaluated ${gate.evaluatedAt}` : ''}
      </Typography>
      {gate.wouldBlock && (
        <Typography sx={{ color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 600, mt: 0.5 }}>
          {gate.blocking
            ? 'Skipped by the relevance gate.'
            : 'This job would have been skipped in enforce mode.'}
        </Typography>
      )}
      {checks.map((c, i) => (
        <Box key={`${c.gate}-${i}`} sx={{ mt: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography component="span" sx={{ color: '#e2e8f0', fontSize: '0.78rem', fontFamily: 'monospace' }}>{c.gate}</Typography>
            <Typography component="span" sx={{ color: GREY, fontSize: '0.78rem', fontWeight: 700 }}>{c.outcome}</Typography>
            {c.reason && (
              <Typography component="span" sx={{ color: GREY, fontSize: '0.74rem' }}>{clip(c.reason)}</Typography>
            )}
          </Box>
          {c.warning && (
            <Typography sx={{ color: GREY, fontSize: '0.72rem', mt: 0.25, pl: 1.5, wordBreak: 'break-word' }}>
              {clip(c.warning)}
            </Typography>
          )}
        </Box>
      ))}
      {codes.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
          {codes.map((code) => (
            <Chip key={code} label={code} size="small" variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem', color: GREY, borderColor: 'rgba(148,163,184,0.35)' }} />
          ))}
        </Box>
      )}
    </CardShell>
  );
}

export function HoldoutCard({ verdict, counts }) {
  if (!verdict) return null;
  const numeric = counts && typeof counts === 'object'
    ? Object.entries(counts).filter(([, v]) => typeof v === 'number')
    : [];
  return (
    <CardShell title="Hold-out check">
      <Typography sx={{ color: GREY, fontSize: '0.85rem', fontWeight: 700 }}>{verdict}</Typography>
      {numeric.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
          {numeric.map(([k, v]) => (
            <Typography key={k} sx={{ color: GREY, fontSize: '0.76rem' }}>{k}: {v}</Typography>
          ))}
        </Box>
      )}
      <Typography sx={{ color: GREY, fontSize: '0.72rem', mt: 0.5 }}>
        Informational only. Not an approval signal.
      </Typography>
    </CardShell>
  );
}
